import type { CSSProperties, FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { AppIcon } from '@/components/generals/app-icon'
import { PillButton } from '@/components/generals/pill-button'
import { useContact } from '@/components/generals/contact-dialog'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import { heroCategories } from '@/constants/full-service'

const TICK = 2000
const SLOT_X = [0, 115, 232, 350, 470]
const SLOT_Y = [0, 17, 31, 48, 62]
const SLOT_ROTATE = [0, 1.5, 3, 4.5, 6]

const slotStyle = (slot: number): CSSProperties => {
    const distance = Math.min(Math.abs(slot), 4)
    const sign = Math.sign(slot)

    return {
        transform: `translate3d(${sign * SLOT_X[distance]}px, ${SLOT_Y[distance]}px, 0) rotate(${sign * SLOT_ROTATE[distance]}deg) perspective(700px) rotateX(48deg) scaleX(${1 + distance * 0.02})`,
        opacity: distance >= 4 ? 0 : 1,
        zIndex: 10 - distance
    }
}

// Seed the belt so every visible card starts with an app from the first categories.
const initialBelt = (): Array<string> => {
    const pool = heroCategories.flatMap(category => category.apps)

    return Array.from({ length: 9 }, (_, index) => pool[(index * 5) % pool.length])
}

const Belt: FC<{ categoryIndex: number, reduced: boolean }> = ({ categoryIndex, reduced }): ReactNode => {
    const [cards, setCards] = useState(initialBelt)
    const [moving, setMoving] = useState(false)
    const [resetting, setResetting] = useState(false)
    const pick = useRef(0)
    const lastIndex = useRef(categoryIndex)

    useEffect(() => {
        if (lastIndex.current === categoryIndex) return

        lastIndex.current = categoryIndex

        if (reduced) return

        const apps = heroCategories[categoryIndex].apps
        const incoming = apps[pick.current % apps.length]

        pick.current += 1

        setCards(previous => [incoming, ...previous.slice(1)])
        setMoving(true)

        const settle = window.setTimeout(() => {
            setResetting(true)
            setMoving(false)
            setCards(previous => [previous[0], ...previous.slice(0, 8)])

            window.requestAnimationFrame(() => {
                window.requestAnimationFrame(() => setResetting(false))
            })
        }, 860)

        return () => window.clearTimeout(settle)
    }, [categoryIndex, reduced])

    return (
        <div aria-hidden='true' className='relative mx-auto h-[150px] w-full max-w-[900px] origin-top scale-[0.72] sm:scale-[0.86] lg:scale-100'>
            {cards.map((app, index) => {
                const slot = index - 4 + (moving ? 1 : 0)
                const distance = Math.abs(slot)

                return (
                    <div
                        key={index}
                        data-resetting={resetting}
                        className='fc-belt-card'
                        style={slotStyle(slot)}
                    >
                        <div
                            className={cn(
                                'flex size-full items-center justify-center rounded-[14px] bg-white shadow-[0_2px_4px_rgba(14,14,14,0.03),0_0_0_1px_rgba(14,14,14,0.08)]',
                                distance === 0 && 'shadow-[0_24px_28px_-10px_rgba(14,14,14,0.16),0_0_0_1px_rgba(14,14,14,0.08)]'
                            )}
                        >
                            <span
                                className='transition-opacity duration-500'
                                style={{ opacity: distance >= 3 ? 0.35 : 1, transition: resetting ? 'none' : undefined }}
                            >
                                <AppIcon name={app} size='lg' />
                            </span>
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

export const FsHero: FC = (): ReactNode => {
    const { openContact } = useContact()
    const reduced = useReducedMotion()
    const [categoryIndex, setCategoryIndex] = useState(0)
    const [previousIndex, setPreviousIndex] = useState<number | null>(null)
    const currentIndex = useRef(0)

    useEffect(() => {
        const timer = window.setInterval(() => {
            const next = (currentIndex.current + 1) % heroCategories.length

            setPreviousIndex(currentIndex.current)
            currentIndex.current = next
            setCategoryIndex(next)
        }, TICK)

        return () => window.clearInterval(timer)
    }, [])

    useEffect(() => {
        if (previousIndex === null) return

        const timer = window.setTimeout(() => setPreviousIndex(null), 480)

        return () => window.clearTimeout(timer)
    }, [previousIndex])

    return (
        <section id='hero' aria-labelledby='fs-hero-title' className='relative -mt-16 overflow-hidden pt-16'>
            <div aria-hidden='true' className='fc-hero-glow' />

            <svg aria-hidden='true' viewBox='0 0 1440 760' preserveAspectRatio='none' className='pointer-events-none absolute inset-x-0 -top-10 h-[760px] w-full'>
                {[-560, -330, -110, 110, 330, 560].map(offset => (
                    <path
                        key={offset}
                        d={`M720 300 L${720 + offset * 3} 760`}
                        stroke='#0e0e0e'
                        strokeOpacity='0.06'
                        vectorEffect='non-scaling-stroke'
                    />
                ))}
            </svg>

            <div className='fc-container relative flex flex-col items-center pb-20 pt-14 text-center md:pb-28 md:pt-20'>
                <div className='fc-hero-in w-full'>
                    <Belt categoryIndex={categoryIndex} reduced={reduced} />
                </div>

                <h1 id='fs-hero-title' className='fc-hero-in mt-6 text-[clamp(2.5rem,6.4vw,4.75rem)] font-medium leading-[1.04] tracking-[-0.045em] text-ink sm:mt-10' style={{ animationDelay: '0.18s' }}>
                    <span className='block'>Full-service integrations for</span>

                    <span className='sr-only'>
                        marketing, ecommerce, finance, logistics, healthcare and field service teams
                    </span>

                    <span aria-hidden='true' className='relative inline-grid min-h-[1.15em] [perspective:600px]'>
                        {previousIndex !== null && !reduced && (
                            <span key={`out-${previousIndex}`} className='fc-word-out fc-text-gradient col-start-1 row-start-1 whitespace-nowrap px-2 pb-[0.08em] font-semibold'>
                                {heroCategories[previousIndex].label}
                            </span>
                        )}

                        <span key={`in-${categoryIndex}`} className='fc-word-in fc-text-gradient col-start-1 row-start-1 whitespace-nowrap px-2 pb-[0.08em] font-semibold'>
                            {heroCategories[categoryIndex].label}
                        </span>
                    </span>
                </h1>

                <p className='fc-hero-in mt-6 max-w-[600px] text-[17px] leading-relaxed text-slate md:text-lg' style={{ animationDelay: '0.45s' }}>
                    FlowCraft gives you a powerful integration platform and a dedicated team that scopes, builds and supports every workflow, and stays with you for the long haul.
                </p>

                <div className='fc-hero-in mt-10' style={{ animationDelay: '0.65s' }}>
                    <PillButton size='lg' chevron onClick={() => openContact({ intent: 'contact' })}>
                        Start the conversation
                    </PillButton>
                </div>
            </div>
        </section>
    )
}
